import React, { useState, useCallback } from 'react'
import { axios } from '../../context/auth'
import InputGroup from '../../styled/input-group'
import { PrimaryButton, SecondaryButton } from '../../styled/button'
import FormError from '../../styled/form-error'
import OptionsContainer, { Title } from '../../styled/options-container'

const GroupWebhookSettings = ({ group, onUpdate }) => {
  const [webhookConfig, setWebhookConfig] = useState(group.webhookConfig || {
    enabled: false,
    platform: null,
    webhookUrl: '',
    notifications: {
      songCreate: false,
      songEdit: false,
      songDelete: false,
      setlistCreate: false,
      setlistEdit: false,
      setlistDelete: false
    }
  })

  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handlePlatformChange = useCallback((platform) => {
    setWebhookConfig({
      ...webhookConfig,
      platform
    })
    setErrors({})
  }, [webhookConfig])

  const handleUrlChange = useCallback((event) => {
    setWebhookConfig({
      ...webhookConfig,
      webhookUrl: event.target.value
    })
    setErrors({})
  }, [webhookConfig])

  const handleNotificationChange = useCallback((notificationType) => {
    setWebhookConfig({
      ...webhookConfig,
      notifications: {
        ...webhookConfig.notifications,
        [notificationType]: !webhookConfig.notifications[notificationType]
      }
    })
  }, [webhookConfig])

  const handleTest = useCallback(async () => {
    setIsLoading(true)
    setErrors({})
    setSuccessMessage('')

    try {
      const response = await axios.post(`/api/groups/${group._id}/webhook/test`)
      setSuccessMessage(response.data.message)
    } catch (error) {
      setErrors({ test: error.response?.data?.msg || 'Failed to send test message' })
    } finally {
      setIsLoading(false)
    }
  }, [group._id])

  const handleSave = useCallback(async () => {
    setIsLoading(true)
    setErrors({})
    setSuccessMessage('')

    try {
      const payload = {
        platform: webhookConfig.platform,
        webhookUrl: webhookConfig.webhookUrl,
        notifications: webhookConfig.notifications
      }
      const response = await axios.put(`/api/groups/${group._id}/webhook`, payload)
      setSuccessMessage('Webhook configuration saved successfully')
      if (onUpdate) {
        onUpdate(response.data)
      }
    } catch (error) {
      setErrors({ save: error.response?.data?.msg || 'Failed to save webhook configuration' })
    } finally {
      setIsLoading(false)
    }
  }, [group._id, webhookConfig, onUpdate])

  const handleRemove = useCallback(async () => {
    if (!window.confirm('Are you sure you want to remove the webhook integration?')) {
      return
    }

    setIsLoading(true)
    setErrors({})
    setSuccessMessage('')

    try {
      const response = await axios.delete(`/api/groups/${group._id}/webhook`)
      setWebhookConfig({
        enabled: false,
        platform: null,
        webhookUrl: '',
        notifications: {
          songCreate: false,
          songEdit: false,
          songDelete: false,
          setlistCreate: false,
          setlistEdit: false,
          setlistDelete: false
        }
      })
      setSuccessMessage('Webhook integration removed successfully')
      if (onUpdate) {
        onUpdate(response.data)
      }
    } catch (error) {
      setErrors({ remove: error.response?.data?.msg || 'Failed to remove webhook integration' })
    } finally {
      setIsLoading(false)
    }
  }, [group._id, onUpdate])

  return (
    <OptionsContainer>
      <Title>Messaging Integration</Title>

      <InputGroup>
        <label htmlFor='platform'>Platform:</label>
        <select
          id='platform'
          value={webhookConfig.platform || ''}
          onChange={(e) => handlePlatformChange(e.target.value || null)}
          disabled={isLoading}
        >
          <option value=''>Select a platform...</option>
          <option value='slack'>Slack</option>
          <option value='discord'>Discord</option>
        </select>
      </InputGroup>

      <InputGroup>
        <label htmlFor='webhookUrl'>Webhook URL:</label>
        <input
          id='webhookUrl'
          type='url'
          value={webhookConfig.webhookUrl}
          onChange={handleUrlChange}
          placeholder={webhookConfig.platform === 'slack' ? 'https://hooks.slack.com/...' : 'https://discord.com/api/webhooks/...'}
          disabled={isLoading || !webhookConfig.platform}
        />
      </InputGroup>

      {webhookConfig.platform && webhookConfig.webhookUrl && (
        <>
          <div style={{ width: '100%', display: 'block', marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', display: 'block', paddingLeft: 0, fontWeight: 600, marginBottom: '0.5rem' }}>
              Notification Events:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem', textTransform: 'none', fontWeight: 'normal' }}>
                <input
                  type='checkbox'
                  checked={webhookConfig.notifications.songCreate}
                  onChange={() => handleNotificationChange('songCreate')}
                  disabled={isLoading}
                  style={{ margin: 0, width: 'auto' }}
                />
                <span>Song Created</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem', textTransform: 'none', fontWeight: 'normal' }}>
                <input
                  type='checkbox'
                  checked={webhookConfig.notifications.songEdit}
                  onChange={() => handleNotificationChange('songEdit')}
                  disabled={isLoading}
                  style={{ margin: 0, width: 'auto' }}
                />
                <span>Song Edited</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem', textTransform: 'none', fontWeight: 'normal' }}>
                <input
                  type='checkbox'
                  checked={webhookConfig.notifications.songDelete}
                  onChange={() => handleNotificationChange('songDelete')}
                  disabled={isLoading}
                  style={{ margin: 0, width: 'auto' }}
                />
                <span>Song Deleted</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem', textTransform: 'none', fontWeight: 'normal' }}>
                <input
                  type='checkbox'
                  checked={webhookConfig.notifications.setlistCreate}
                  onChange={() => handleNotificationChange('setlistCreate')}
                  disabled={isLoading}
                  style={{ margin: 0, width: 'auto' }}
                />
                <span>Setlist Created</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem', textTransform: 'none', fontWeight: 'normal' }}>
                <input
                  type='checkbox'
                  checked={webhookConfig.notifications.setlistEdit}
                  onChange={() => handleNotificationChange('setlistEdit')}
                  disabled={isLoading}
                  style={{ margin: 0, width: 'auto' }}
                />
                <span>Setlist Edited</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem', textTransform: 'none', fontWeight: 'normal' }}>
                <input
                  type='checkbox'
                  checked={webhookConfig.notifications.setlistDelete}
                  onChange={() => handleNotificationChange('setlistDelete')}
                  disabled={isLoading}
                  style={{ margin: 0, width: 'auto' }}
                />
                <span>Setlist Deleted</span>
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <PrimaryButton onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Configuration'}
            </PrimaryButton>
            <SecondaryButton onClick={handleTest} disabled={isLoading}>
              {isLoading ? 'Testing...' : 'Test Integration'}
            </SecondaryButton>
            {webhookConfig.enabled && (
              <SecondaryButton onClick={handleRemove} disabled={isLoading}>
                Remove Integration
              </SecondaryButton>
            )}
          </div>
        </>
      )}

      {errors.save && <FormError>{errors.save}</FormError>}
      {errors.test && <FormError>{errors.test}</FormError>}
      {errors.remove && <FormError>{errors.remove}</FormError>}
      {successMessage && <div style={{ color: 'green', marginTop: '1rem' }}>{successMessage}</div>}
    </OptionsContainer>
  )
}

export default GroupWebhookSettings
